// Frame-pacing judgement for the landing page's adaptive resolution. No DOM,
// no three.js: the decision is a pure function of frame intervals, so node
// tests can feed it recorded sequences.
//
// Feed it the interval (ms) after every frame, saying whether that frame
// rendered. It answers "is the GPU genuinely missing frames?", and
// deliberately not "is the frame rate low?": a browser that caps rAF at 30 Hz
// (Low Power Mode, Energy Saver, a 30 Hz display) delivers a steady ~33.3 ms
// cadence however light the page is, and lowering the resolution there costs
// sharpness for nothing.
//
// The cadence is what the loop gets without GPU work: the median interval
// after frames that didn't render (render-on-demand idles between moves),
// which shows a rAF cap and the display's refresh alike. Until such frames
// are seen, it falls back to the fastest regular interval of the rendered
// frames (a low percentile), so a steady cadence then reads as a cap. A
// rendered frame missed when its interval exceeds 1.5 cadences and
// PACING.floorMs (the old ~38 fps threshold). A window of
// rendered frames is slow when more than a quarter missed; two slow windows
// in a row mean slow. One-off stalls (a shader compile, a texture upload, a
// GC) are a few missed frames in a window, not a quarter of it.

export const PACING = {
  windowFrames: 60,
  missedLimit: 0.25,
  slowWindows: 2,
  cadencePercentile: 0.1,
  idleFrames: 30, // idle intervals kept for the cadence
  // frames faster than this (~38 fps) never count as missed: a GPU keeping
  // 60 fps on a 120 Hz display is not slow
  floorMs: 1000 / 38,
};

const sorted = (xs) => [...xs].sort((a, b) => a - b);
const percentile = (xs, q) => sorted(xs)[Math.floor((xs.length - 1) * q)];

// the share of rendered `intervals` that missed `cadence` (default: the
// cadence the intervals themselves establish)
export function missedShare(intervals, cadence = percentile(intervals, PACING.cadencePercentile)) {
  return intervals.filter((dt) => dt > cadence * 1.5 && dt > PACING.floorMs).length / intervals.length;
}

// Stateful monitor over consecutive windows. add(dt, rendered) returns true
// once, when the pacing has been slow for PACING.slowWindows windows in a row.
export function createPacingMonitor() {
  let window = [];
  let idle = [];
  let slow = 0;
  return {
    add(dt, rendered) {
      if (!(dt > 0)) throw new RangeError(`frame interval must be positive, got ${dt}`);
      if (typeof rendered !== "boolean") throw new TypeError(`rendered must be a boolean, got ${rendered}`);
      if (!rendered) {
        idle.push(dt);
        if (idle.length > PACING.idleFrames) idle.shift();
        return false;
      }
      window.push(dt);
      if (window.length < PACING.windowFrames) return false;
      const own = percentile(window, PACING.cadencePercentile);
      const cadence = idle.length >= PACING.idleFrames ? Math.min(own, percentile(idle, 0.5)) : own;
      slow = missedShare(window, cadence) > PACING.missedLimit ? slow + 1 : 0;
      window = [];
      return slow >= PACING.slowWindows;
    },
    // whether enough idle frames were seen to know the rAF cadence
    get cadenceKnown() {
      return idle.length >= PACING.idleFrames;
    },
    // the rAF cadence (ms: the median idle interval), or null until known
    get cadenceMs() {
      return idle.length >= PACING.idleFrames ? percentile(idle, 0.5) : null;
    },
    // forget everything (after a pause, the loop restarting)
    reset() {
      window = [];
      idle = [];
      slow = 0;
    },
  };
}

// rendered frames needed for a verdict: a page that renders on demand can run
// this many (identical) frames once, early, to find out before the visitor
// starts moving things on a GPU that can't keep up
export const PROBE_FRAMES = PACING.windowFrames * PACING.slowWindows;

// whether a recorded sequence would downshift: `frames` are intervals of
// rendered frames, or { dt, rendered } for a mix
export function wouldDownshift(frames) {
  const monitor = createPacingMonitor();
  return frames.some((f) => (typeof f === "number" ? monitor.add(f, true) : monitor.add(f.dt, f.rendered)));
}

// --- the frame cap ------------------------------------------------------------
//
// The landing page draws at most `fps` frames a second (0: as many as rAF
// gives). Frames are due every 1/fps s, and a frame runs on the display tick
// nearest its due time: a tick more than half a display tick early waits for
// the next. So a display that ticks at a multiple of the cap gets a steady
// rate — every other tick at 120 Hz for a cap of 60 (not a jittery mix of 60
// and 40), every tick at 60 Hz — however much the tick timestamps wobble,
// and one that doesn't (144 Hz) averages the cap. The due times follow the
// ticks they land on a little each frame (PHASE_GAIN), so a display a hair
// off a multiple of the cap stays in step with it; a frame that lands a tick
// late or more (a dropped vsync, a stall) moves the schedule on by those
// ticks rather than hurrying the next. The display's tick is the lower
// quartile of the recent rAF intervals: Chrome skips vsyncs (longer
// intervals) and delivers some ticks late (a long interval, then a short
// one), so the middle can overstate it.
//
// Chrome's rAF timestamps aren't vsync times: a tick delivered most of half
// a tick late can't be told from its neighbour, so a few frames in a hundred
// still come a tick early or late (tests/frame-pacing.test.mjs models it).
//
// The kept ticks are the page's frames, and the pacing monitor above judges
// those: a cap is a steady cadence, not slowness. (Change the cap and the
// cadence changes with it: reset the monitor too.)
export const PHASE_GAIN = 0.1;
const TICKS_KEPT = 45;
const TICK_PERCENTILE = 0.25;

export function createFrameCap(fps) {
  let cap = 0;
  let interval = 0;
  let due = null; // when the next frame is due (rAF time, ms)
  let prevTick = null;
  let ticks = []; // the latest rAF intervals, ms
  const self = {
    get fps() {
      return cap;
    },
    set fps(value) {
      if (!(value >= 0 && Number.isFinite(value))) throw new RangeError(`fps cap must be 0 (none) or positive, got ${value}`);
      cap = value;
      interval = value ? 1000 / value : 0;
      due = null;
    },
    /** Whether to run the frame at rAF time `now` (ms); call on every tick. */
    keep(now) {
      if (prevTick !== null && now > prevTick) {
        ticks.push(now - prevTick);
        if (ticks.length > TICKS_KEPT) ticks.shift();
      }
      prevTick = now;
      if (!interval) return true;
      const tick = ticks.length ? percentile(ticks, TICK_PERCENTILE) : 0;
      if (due === null || now - due > interval) {
        // the first frame, or one after a stall: the schedule starts here
        due = now + interval;
        return true;
      }
      if (now < due - tick / 2) return false;
      const late = now - due;
      // a tick delivered late (by up to ~0.6 of a tick, measured) is still
      // its vsync's; most of a tick late, it's a later vsync's
      due += interval + (late < tick * 0.75 ? late * PHASE_GAIN : Math.round(late / tick) * tick);
      return true;
    },
    /** Start afresh (the loop paused): the next tick runs. */
    reset() {
      due = null;
      prevTick = null;
      ticks = [];
    },
  };
  self.fps = fps;
  return self;
}

// After IDLE_AFTER_MS without input the page caps itself at IDLE_FPS: the
// turntable keeps turning (motion is per second, see motion.js), in half the
// frames. Any input lifts it.
export const IDLE_AFTER_MS = 60_000;
export const IDLE_FPS = 30;

/** The cap (0: none) for a page whose last input was `idleMs` ago. */
export function capWhenIdle(fps, idleMs) {
  if (idleMs < IDLE_AFTER_MS) return fps;
  return fps === 0 ? IDLE_FPS : Math.min(fps, IDLE_FPS);
}
